<template>
  <div
    class="call-item"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
    tabindex="0"
    role="button"
  >
    <VcTooltip dark :content="callerName">
      <div class="call-item__icon">
        <CallAnsweredByAppIcon v-if="isAnsweredCallByApp" class="call-item__icon-svg" />
        <CallAnsweredIcon v-else-if="isAnsweredCallByStaff" class="call-item__icon-svg" />
        <CallVoicemailIcon v-else-if="isVoicemail" class="call-item__icon-svg" />
        <CallMissedIcon v-else class="call-item__icon-svg" />
      </div>
    </VcTooltip>
    <div class="call-item__content">
      <div class="call-item__details">
        <div class="call-item__caller">
          {{ callerName }}
        </div>
        <VcTooltip dark :content="callStatusText">
          <div class="call-item__status">
            {{ callStatusText }}
          </div>
        </VcTooltip>
        <div class="call-item__time">
          {{ formattedTime }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CallAnsweredByAppIcon from '@/assets/images/widgets/call-answered-by-app.svg';
import CallAnsweredIcon from '@/assets/images/widgets/call-answered-icon.svg';
import CallMissedIcon from '@/assets/images/widgets/call-missed-icon.svg';
import CallVoicemailIcon from '@/assets/images/widgets/call-voicemail-icon.svg';

const CALL_STATUS = {
  ANSWERED_BY_STAFF: 'answered_by_staff',
  ANSWERED_BY_CALLS_PROVIDER: 'answered_by_calls_provider',
  VOICEMAIL: 'voicemail',
  MISSED_CALL_HANDLED: 'missed_call_handled',
  MISSED: 'missed',
};

export default {
  name: 'CallItem',
  components: {
    CallAnsweredIcon,
    CallMissedIcon,
    CallVoicemailIcon,
    CallAnsweredByAppIcon,
  },
  props: {
    call: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isAnsweredCallByApp() {
      return this.call.status === CALL_STATUS.ANSWERED_BY_CALLS_PROVIDER;
    },
    isAnsweredCallByStaff() {
      return this.call.status === CALL_STATUS.ANSWERED_BY_STAFF;
    },
    isVoicemail() {
      return this.call.status === CALL_STATUS.VOICEMAIL;
    },
    callerName() {
      return this.call.caller_name || this.call.caller_number || this.$t('widget.calls_widget.unknown_caller');
    },
    callStatusText() {
      const { status } = this.call;

      if (status === CALL_STATUS.ANSWERED_BY_STAFF) {
        if (this.call.staff_name) {
          return this.$t('widget.calls_widget.answered_by_staff_name', { staffName: this.call.staff_name });
        }
        return this.$t('widget.calls_widget.answered_by_staff');
      }
      if (status === CALL_STATUS.ANSWERED_BY_CALLS_PROVIDER) {
        if (this.call.app_name) {
          return this.$t('widget.calls_widget.answered_by_app_name', { appName: this.call.app_name });
        }
        return this.$t('widget.calls_widget.answered_by_provider');
      }
      if (status === CALL_STATUS.VOICEMAIL) {
        return this.$t('widget.calls_widget.voicemail');
      }
      if (status === CALL_STATUS.MISSED_CALL_HANDLED) {
        return this.$t('widget.calls_widget.missed_call_handled');
      }
      if (status === CALL_STATUS.MISSED) {
        return this.$t('widget.calls_widget.missed_call');
      }
      return status;
    },
    formattedTime() {
      if (!this.call.created_at) {
        return '';
      }

      const callDate = new Date(this.call.created_at);
      const now = new Date();

      // Check if it's today
      const isToday = callDate.toDateString() === now.toDateString();

      // Check if it's yesterday
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = callDate.toDateString() === yesterday.toDateString();

      // Format time as 12-hour format with am/pm
      let hours = callDate.getHours();
      const minutes = callDate.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours %= 12;
      hours = hours || 12; // 0 should be 12

      let dateLabel;
      if (isToday) {
        dateLabel = 'TODAY';
      } else if (isYesterday) {
        dateLabel = 'YESTERDAY';
      } else {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[callDate.getMonth()];
        const day = callDate.getDate();
        dateLabel = `${month} ${day}`;
      }

      return `${dateLabel},\n${hours}:${minutes} ${ampm}`;
    },
  },
};
</script>

<style scoped lang="scss">
.call-item {
  display: flex;
  align-items: center;
  gap: var(--size-value3);
  padding: var(--size-value2) var(--size-value4);
  background-color: var(--gray-lighten-4);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background-color 0.2s ease;
  height: 60px;
  box-sizing: border-box;

  &:hover {
    background-color: var(--gray-lighten-3);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &__icon-svg {
    width: 20px;
    height: 20px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
  }

  &__caller {
    width: 90px;
    font-size: var(--font-size-xx-small);
    font-weight: var(--font-weight-large);
    line-height: 16px;
    color: var(--gray-darken-5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    width: 80px;
    font-size: var(--font-size-xs-small);
    font-weight: var(--font-weight-medium);
    line-height: 14px;
    color: var(--gray-darken-5);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    max-height: 28px; /* 2 lines * 14px line-height */
  }

  &__time {
    width: 80px;
    font-size: 10px;
    font-weight: var(--font-weight-large);
    line-height: 16px;
    color: var(--gray-darken-4);
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: pre-line;
  }
}
</style>
